import logger from '@/logger'
import { Device, Sensor } from '@/store/modules/measurements'

export class DeviceRelation {
  devices
  id
  label
  sensorRelations
  constructor({ devices, sensorRelations }) {
    if (!devices.every((device) => device instanceof Device)) throw new TypeError('invalid Device given')
    if (!sensorRelations.every((sensorRelations) => sensorRelations instanceof SensorRelation))
      throw new TypeError('invalid SensorRelation given')

    this.devices = devices.sort((a, b) => {
      if (a?.isOfType('physical_device')) return -1
      if (b?.isOfType('physical_device')) return 1
      return 0
    })
    this.sensorRelations = sensorRelations
    this.id = this.devices[0].id
    this.label = this.devices[0].label
  }

  deviceIds() {
    return this.devices.map((device) => device.id)
  }
  sensorIds({ sensorTypeRegExp, type } = {}) {
    return this.sensorRelations.reduce(
      (previousSensorIds, sensorRelation) => [
        ...previousSensorIds,
        ...sensorRelation.sensorIds({ sensorTypeRegExp, type })
      ],
      []
    )
  }
  sensorLabels({ whitelistedSensorIds } = {}) {
    return this.sensorRelations
      .map((sR) => {
        if (!whitelistedSensorIds) return sR.label

        if (sR.sensorIds().some((id) => whitelistedSensorIds.includes(id))) return sR.label
      })
      .filter((x) => x !== undefined)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  }
}

export class SensorRelation {
  id
  label
  sensors
  constructor({ sensors }) {
    if (!sensors.every((sensor) => sensor instanceof Sensor)) throw new TypeError('invalid Sensor given')

    this.sensors = sensors.sort((a, b) => {
      if (a?.isOfType('reference') || a?.isOfType('typed')) return -1
      if (b?.isOfType('reference') || b?.isOfType('typed')) return 1
      return 0
    })
    this.id = this.sensors[0].id
    this.label = this.sensors[0].label
  }

  hasSensorWithSensorType({ sensorTypeRegExp = null } = {}) {
    return this.sensors.some((s) => s.sensorTypeId.match(sensorTypeRegExp))
  }
  hasSensorWithType({ type = null } = {}) {
    return this.sensors.some((s) => s.type === type)
  }
  sensorIds({ sensorTypeRegExp, type } = {}) {
    if (sensorTypeRegExp && !this.hasSensorWithSensorType({ sensorTypeRegExp })) return []
    if (type && !this.hasSensorWithType({ type })) return []

    return this.sensors.map((sensor) => parseInt(sensor.id))
  }
}

export const devicesAndSensorsToRelations = ({ devices, sensors }) => {
  const sensorRelations = {}
  const deviceRelations = {}

  const { otherSensors, refOrTypedSensors } = groupSensors({ sensors })

  setRefOrTypedSensorRelations({ otherSensors, refOrTypedSensors, sensorRelations })

  for (const sensor of Object.values(otherSensors)) {
    if (sensorRelations[sensor.id]) continue

    const sensorRelation = new SensorRelation({ sensors: [sensor] })
    sensorRelations[sensorRelation.id] = sensorRelation
  }

  const { logicalDevices, physicalDevices } = groupAndFilterDevices({ devices })

  setLogicalDeviceRelations({ deviceRelations, logicalDevices, physicalDevices, sensorRelations })

  for (const device of Object.values(physicalDevices)) {
    const deviceSensorRelations = findSensorRelationsForDevices({
      devices: [device],
      sensorRelations
    })
    const deviceRelation = new DeviceRelation({
      devices: [device],
      sensorRelations: deviceSensorRelations
    })
    deviceRelations[deviceRelation.id] = deviceRelation
  }

  return deviceRelations
}

export const groupSensors = ({ sensors }) => {
  const refOrTypedSensors = []
  const otherSensors = {}
  for (const sensor of sensors) {
    if (!(sensor instanceof Sensor)) continue

    if (sensor.isOfType('reference') || sensor.isOfType('typed')) {
      refOrTypedSensors.push(sensor)
    } else {
      otherSensors[sensor.compositeId] = sensor // needed for instant update mapping
      otherSensors[sensor.id] = sensor
    }
  }
  return { refOrTypedSensors, otherSensors }
}

export const setRefOrTypedSensorRelations = ({ otherSensors, refOrTypedSensors, sensorRelations }) => {
  for (const sensor of refOrTypedSensors) {
    const relationSensors = [sensor]
    const instantSensorId = sensor.compositeId
      .split('_')
      .map((x, i) => {
        if (i === 2) return parseInt(x) + 1000
        return x
      })
      .join('_')
    for (const refId of [sensor.refId, instantSensorId]) {
      if (otherSensors[refId]) {
        const relationSensor = otherSensors[refId]
        relationSensors.push(relationSensor)
        delete otherSensors[relationSensor.compositeId]
        delete otherSensors[relationSensor.id]
      }
    }

    const sensorRelation = new SensorRelation({ sensors: relationSensors })
    sensorRelations[sensorRelation.id] = sensorRelation
  }
}

export const groupAndFilterDevices = ({ devices }) => {
  const logicalDevices = []
  const physicalDevices = {}
  for (const device of devices) {
    if (!(device instanceof Device)) continue

    if (device.isOfType('iec_logical_device')) {
      if (device.children?.length > 1) continue

      logicalDevices.push(device)
    } else if (device.isOfType('physical_device')) {
      physicalDevices[device.id] = device
    } else {
      logger.info('Skipping device with unknown flag', device)
    }
  }
  return { logicalDevices, physicalDevices }
}

export const findSensorRelationsForDevices = ({ devices, sensorRelations }) => {
  const relationDevicesSensorRelations = []
  for (const device of devices) {
    for (const sensorId of device.sensors) {
      if (sensorRelations[sensorId]) relationDevicesSensorRelations.push(sensorRelations[sensorId])
    }
  }
  return relationDevicesSensorRelations
}

export const setLogicalDeviceRelations = ({ deviceRelations, logicalDevices, physicalDevices, sensorRelations }) => {
  const deviceGroups = []
  for (const device of logicalDevices) {
    const relationDevices = []

    if (device.children?.length) {
      const childId = device.children[0]
      const physicalDevice = physicalDevices[childId]

      if (physicalDevice) {
        relationDevices.push(physicalDevice)
        delete physicalDevices[physicalDevice.id]
        relationDevices.push(device)
      } else {
        const groupIndex = deviceGroups.findIndex((group) => group.some((device) => device.id === childId))
        deviceGroups[groupIndex].push(device)
        continue
      }
    } else {
      relationDevices.push(device)
    }

    deviceGroups.push(relationDevices)
  }

  for (const devices of deviceGroups) {
    const relationDevicesSensorRelations = findSensorRelationsForDevices({
      devices,
      sensorRelations
    })
    const deviceRelation = new DeviceRelation({
      devices,
      sensorRelations: relationDevicesSensorRelations
    })
    deviceRelations[deviceRelation.id] = deviceRelation
  }
}
